/**
 * Tenant detection utility for QPG University.
 * Extracts the tenant slug from the active browser subdomain.
 */

const RESERVED_SUBDOMAINS = ['www', 'api', 'app', 'admin'];

// Full hostnames that serve the super admin portal without an admin.* subdomain
// (e.g. "unifiedqpg.worldfellow.com"). Comma-separated, baked in at build time.
const ADMIN_HOSTS = (process.env.REACT_APP_ADMIN_HOSTS || '')
  .split(',')
  .map(h => h.trim().toLowerCase())
  .filter(Boolean);

export function isAdminHost() {
  const hostname = window.location.hostname.toLowerCase();
  return hostname.startsWith('admin.') || ADMIN_HOSTS.includes(hostname);
}

export function getTenantFromSubdomain() {
  const hostname = window.location.hostname;
  if (ADMIN_HOSTS.includes(hostname.toLowerCase())) return 'admin';

  const parts = hostname.split('.');

  // Examples:
  // - "sdbi.localhost:3000" -> hostname: "sdbi.localhost" -> parts: ["sdbi", "localhost"]
  // - "pune-uni.studentscenter.in" -> parts: ["pune-uni", "studentscenter", "in"]
  // - "localhost:3000" -> hostname: "localhost" -> parts: ["localhost"]
  if (parts.length >= 2) {
    const subdomain = parts[0].toLowerCase().trim();

    // Exclude reserved system-level subdomains except admin
    if (['www', 'api', 'app'].includes(subdomain)) {
      return null;
    }

    // For localhost with no subdomain, we return null.
    // Wait, if parts is just ["localhost"], parts.length is 1.
    // So if parts is ["admin", "localhost"], it returns "admin".
    return subdomain;
  }

  return null;
}

function normalizeDomain(value) {
  return value
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .trim();
}

// Resolves the apex domain that tenant subdomains hang off of, robustly across
// multi-label roots (e.g. "qpg.studentscenter.in") where naive last-2-labels
// slicing would wrongly drop the "qpg" label.
export function getRootDomain() {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return 'localhost';
  }

  const envDomain = process.env.REACT_APP_ROOT_DOMAIN;
  if (envDomain && normalizeDomain(envDomain) && normalizeDomain(envDomain) !== 'localhost') {
    return normalizeDomain(envDomain);
  }

  const parts = hostname.split('.');
  // The super admin always sits on the apex domain or a reserved subdomain
  // (e.g. "admin"). Strip only a leading reserved label; never assume the
  // root is the last two labels.
  if (parts.length > 2 && RESERVED_SUBDOMAINS.includes(parts[0].toLowerCase())) {
    return parts.slice(1).join('.');
  }
  return hostname;
}

export function getTenantUrl(slug) {
  if (!slug) return '';
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//${slug}.${getRootDomain()}${port}`;
}

export function getAdminUrl() {
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  if (ADMIN_HOSTS.length) return `${protocol}//${ADMIN_HOSTS[0]}${port}`;
  return `${protocol}//admin.${getRootDomain()}${port}`;
}
